// frontend/src/utils/csrfFetch.js
//
// The backend enforces double-submit CSRF in production: every
// POST/PUT/PATCH/DELETE to /api must carry an X-CSRF-Token header matching
// the scr_csrf cookie. Many components still call raw fetch() with only an
// Authorization header, which the server rejects with 403 CSRF_FAILED.
//
// Rather than migrating every call site, this patches window.fetch once so
// write requests to our own API get the token attached automatically.
// Requests that already set X-CSRF-Token, and requests to other hosts, are
// left untouched.

import { API_BASE_URL } from './index';

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function readCsrfCookie() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)scr_csrf=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

function isApiUrl(url) {
  if (!url) return false;
  try {
    const target = new URL(url, window.location.href);
    const api = new URL(API_BASE_URL, window.location.href);
    return target.origin === api.origin && target.pathname.startsWith(api.pathname);
  } catch {
    return false;
  }
}

export function installCsrfFetch() {
  if (typeof window === 'undefined' || window.__scrCsrfFetchInstalled) return;
  window.__scrCsrfFetchInstalled = true;

  const nativeFetch = window.fetch.bind(window);
  let priming = null;

  const getToken = async () => {
    const existing = readCsrfCookie();
    if (existing) return existing;
    if (!priming) {
      priming = nativeFetch(`${API_BASE_URL}/csrf`, { credentials: 'include' })
        .then(r => (r.ok ? r.json() : null))
        .then(j => (j && j.csrfToken) || readCsrfCookie())
        .catch(() => readCsrfCookie())
        .finally(() => { priming = null; });
    }
    return priming;
  };

  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' || input instanceof URL ? String(input) : input?.url;
    const method = (init.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();

    if (!WRITE_METHODS.has(method) || !isApiUrl(url)) {
      return nativeFetch(input, init);
    }

    const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
    if (!headers.has('X-CSRF-Token')) {
      const token = await getToken();
      if (token) headers.set('X-CSRF-Token', token);
    }

    return nativeFetch(input, {
      ...init,
      headers,
      credentials: init.credentials || 'include',
    });
  };
}
